// src/hooks/useFinanceResources.js
//
// Global `finance_resources` plus the org's private overlay, merged — the same
// two-listener shape Brain Health uses, lifted into a hook so the adult hub,
// the teen surface and Kidz Corner all read one list and filter by tier.

import { useState, useEffect } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { FINANCE_LIBRARY_ID } from '../data/financeHub';

export function useFinanceResources(orgId) {
    const [globalItems, setGlobalItems] = useState([]);
    const [privateItems, setPrivateItems] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const unsub = onSnapshot(
            collection(db, FINANCE_LIBRARY_ID),
            (snap) => {
                setGlobalItems(snap.docs.map((d) => ({ id: d.id, ...d.data(), isPrivate: false })));
                setLoaded(true);
            },
            (err) => {
                // Rules not deployed yet, or offline — an empty hub beats a crash.
                console.warn('finance_resources listener failed', err);
                setLoaded(true);
            }
        );
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!orgId) { setPrivateItems([]); return undefined; }
        const ref = doc(db, 'organizations', orgId, 'private_libraries', FINANCE_LIBRARY_ID);
        const unsub = onSnapshot(
            ref,
            (snap) => {
                const list = snap.exists() ? (snap.data().resources || []) : [];
                setPrivateItems(list.map((r) => ({ ...r, isPrivate: true })));
            },
            () => setPrivateItems([])
        );
        return () => unsub();
    }, [orgId]);

    // Private overlay wins on id collision, so an org can override a global item.
    const byId = new Map();
    globalItems.forEach((r) => byId.set(r.id, r));
    privateItems.forEach((r) => byId.set(r.id, r));
    const resources = Array.from(byId.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return { resources, loaded };
}
